import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  MutableRefObject,
} from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import ScrollableGameSection from "./ScrollableGameSection";
import Sidebar from "./SideBar";
import { fetchHtml5Categories } from "../api/html5CategoryApi";
import { fetchGamesByCategory } from "../api/html5GamesApi";
import RoundLoader from "../common/RoundLoader";
import TopGames from "./TopGames";
import GameListSection from "./GameListSection";
import { url } from "../api/url";

interface Category {
  _id: string;
  name: string;
  featuredImage?: {
    default?: string;
  };
}

interface Game {
  _id?: string;
  name: string;
  title?: string;
  image?: string;
  isFallback?: boolean;
  thumbImage?: {
    default?: string;
  };
}

interface ScrollState {
  canScrollLeft: boolean;
  canScrollRight: boolean;
}

interface GameListCategory {
  name: string;
  games: Game[];
}

const toSlug = (str = "") =>
  str.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

const createScrollable = (ref: MutableRefObject<HTMLDivElement | null>) => {
  const scroll = (dir: "left" | "right") => {
    const el = ref.current;
    if (!el) return;
    el.scrollBy({ left: dir === "left" ? -300 : 300, behavior: "smooth" });
  };

  const getScrollState = (): ScrollState => {
    const el = ref.current;
    if (!el) return { canScrollLeft: false, canScrollRight: false };
    return {
      canScrollLeft: el.scrollLeft > 0,
      canScrollRight: el.scrollLeft + el.clientWidth < el.scrollWidth,
    };
  };

  return { scroll, getScrollState };
};

const CategoryWiseGames: React.FC = () => {
  const { t } = useTranslation();
  const [categories, setCategories] = useState<Category[]>([]);
  const [gamesByCat, setGamesByCat] = useState<Record<string, Game[]>>({});
  const [refs, setRefs] = useState<MutableRefObject<HTMLDivElement | null>[]>([]);
  const [scrollStates, setScrollStates] = useState<ScrollState[]>([]);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [skip, setSkip] = useState(0);
  const limit = 3;
  const [hasMore, setHasMore] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState<GameListCategory | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const navigate = useNavigate();
  const effectRan = useRef(false);

  const loadMoreCategories = useCallback(async (isFirstTime = false) => {
    if (isFirstTime) setInitialLoading(true);
    else setLoading(true);

    try {
      const response = await fetchHtml5Categories({ skip, limit });
      const newCats: Category[] = response?.list || [];

      if (newCats.length < limit) setHasMore(false);

      setCategories((prev) => [...prev, ...newCats]);
      setRefs((prev) => [...prev, ...newCats.map(() => React.createRef<HTMLDivElement>())]);
      setScrollStates((prev) => [
        ...prev,
        ...newCats.map(() => ({ canScrollLeft: false, canScrollRight: true })),
      ]);

      const updatedGames: Record<string, Game[]> = { ...gamesByCat };
      await Promise.all(
        newCats.map(async (cat) => {
          const res = await fetchGamesByCategory(cat._id);
          updatedGames[cat._id] = res?.list || [];
        })
      );
      setGamesByCat(updatedGames);
      setSkip((prev) => prev + limit);
    } catch (err) {
      console.error("Error loading:", err);
    } finally {
      if (isFirstTime) setInitialLoading(false);
      else setLoading(false);
    }
  }, [skip, limit, gamesByCat]);

  useEffect(() => {
    if (!effectRan.current) {
      loadMoreCategories(true);
      effectRan.current = true;
    }
  }, [loadMoreCategories]);

  const handleScroll = (index: number, direction: "left" | "right") => {
    const ref = refs[index];
    if (!ref?.current) return;
    const { scroll, getScrollState } = createScrollable(ref);
    scroll(direction);
    setTimeout(() => {
      const updated = [...scrollStates];
      updated[index] = getScrollState();
      setScrollStates(updated);
    }, 300);
  };

  return (
    <>
      {initialLoading && <RoundLoader size={8} color="#f92c2c" borderWidth={5} />}

      <div className={`flex bg-white text-white min-h-screen font-proxima transition-all duration-300 ${initialLoading ? "opacity-20 blur-sm pointer-events-none" : ""}`}>
        <Sidebar
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          setSidebarOpen={setSidebarOpen}
        />
       <div className="flex-1 overflow-x-hidden p-4 bg-gamerDark">
          <div className="max-w-screen-xl mx-auto">
            <TopGames />

            {categories.map((cat, index) => {
              let games = gamesByCat[cat._id] || [];

              if (games.length === 0 && cat?.featuredImage?.default) {
                games = [
                  {
                    name: `${cat.name} (${t("categoryWise.comingSoon")})`,
                    image: cat.featuredImage.default,
                    isFallback: true,
                  },
                ];
              }

              const ref = refs[index];
              const scrollState = scrollStates[index] || {
                canScrollLeft: false,
                canScrollRight: true,
              };

              return (
                <ScrollableGameSection
                  key={cat._id}
                  title={`🎮 ${cat.name}`}
                  games={games.map((g) => ({
                    ...g,
                    title: g.name,
                    image: g.isFallback
                      ? `${url.imgUrl}/${g.image}`
                      : `${url.imgUrl}/${g.thumbImage?.default || ""}`,
                  }))}
                  refObj={ref}
                 scrollHandler={(dir: "left" | "right") => handleScroll(index, dir)}
                  canScrollLeft={scrollState.canScrollLeft}
                  canScrollRight={scrollState.canScrollRight}
                  viewAllLink={() => navigate(`/category/${toSlug(cat.name)}`)}
                  onCardClick={(game) =>
                    navigate(`/game/${toSlug(game.name)}`, { state: { game } })
                  }
                />
              );
            })}

            {selectedCategory && (
              <GameListSection
                categoryName={selectedCategory.name}
                games={selectedCategory.games}
                onBack={() => setSelectedCategory(null)}
              />
            )}
            {loading && !initialLoading && (
              <div className="flex justify-center mt-4">
                <RoundLoader size={6} color="#f92c2c" borderWidth={4} />
              </div>
            )}

            {!loading && hasMore && (
              <div className="flex justify-center mt-6">
                <button
                  className="px-6 py-2 bg-[#f92c2c] text-white rounded hover:bg-[#d92323] transition"
                  onClick={() => loadMoreCategories(false)}
                >
                  {t("categoryWise.loadMore")}
                </button>
              </div>
            )}

            {!loading && !hasMore && (
              <p className="text-[#070b28] text-center mt-4">
                {t("categoryWise.noMore")}
              </p>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default CategoryWiseGames;
